/**
 * Decodifica TIFF en el cliente con UTIF.js y rasteriza a PNG (data URL)
 * para poder mostrarlo en &lt;img&gt; (los navegadores no renderizan TIFF nativamente).
 */

import * as UTIF from 'utif';

/** Límite para evitar bloqueos / memoria excesiva en TIFF muy grandes. */
const MAX_PREVIEW_SIDE_PX = 8192;
const MAX_PREVIEW_PIXELS = 45_000_000;

export async function tiffBlobUrlToPngDataUrl(blobUrl: string): Promise<string> {
  const res = await fetch(blobUrl);
  if (!res.ok) {
    throw new Error('No se pudo leer el archivo para la vista previa.');
  }
  const buffer = await res.arrayBuffer();
  return tiffArrayBufferToPngDataUrl(buffer);
}

export function tiffArrayBufferToPngDataUrl(buffer: ArrayBuffer): string {
  const ifds = UTIF.decode(buffer) as TiffIfd[];
  if (!ifds?.length) {
    throw new Error('El TIFF no contiene imágenes.');
  }

  const u8 = new Uint8Array(buffer);
  const page = ifds[0];
  UTIF.decodeImage(u8, page, ifds as never);

  const w = page.width;
  const h = page.height;
  if (!w || !h) {
    throw new Error('No se pudieron leer las dimensiones del TIFF.');
  }
  if (w * h > MAX_PREVIEW_PIXELS) {
    throw new Error('La imagen es demasiado grande para previsualizarla en el navegador.');
  }

  const rgba = UTIF.toRGBA8(page);
  const source = document.createElement('canvas');
  source.width = w;
  source.height = h;
  const sctx = source.getContext('2d');
  if (!sctx) {
    throw new Error('No se pudo preparar el lienzo de vista previa.');
  }
  const imgData = sctx.createImageData(w, h);
  imgData.data.set(rgba);
  sctx.putImageData(imgData, 0, 0);

  const maxSide = Math.max(w, h);
  if (maxSide <= MAX_PREVIEW_SIDE_PX) {
    return source.toDataURL('image/png');
  }

  const scale = MAX_PREVIEW_SIDE_PX / maxSide;
  const tw = Math.max(1, Math.floor(w * scale));
  const th = Math.max(1, Math.floor(h * scale));
  const out = document.createElement('canvas');
  out.width = tw;
  out.height = th;
  const octx = out.getContext('2d');
  if (!octx) {
    throw new Error('No se pudo redimensionar la vista previa.');
  }
  octx.imageSmoothingEnabled = true;
  octx.imageSmoothingQuality = 'high';
  octx.drawImage(source, 0, 0, tw, th);
  return out.toDataURL('image/png');
}

/** Forma mínima del IFD que expone UTIF tras decodeImage. */
interface TiffIfd {
  width: number;
  height: number;
  data?: Uint8Array;
  [key: string]: unknown;
}
